/**
 * Where each space type is offered: in each of the wizard's modes (full room,
 * ceiling only, individual walls) and in Advanced mode -- the admin's
 * switches (app setting `spaceTypeAccess`, Admin → Settings → Space types).
 * A space with no entry, or no entry for a use, is offered. Switching one
 * off hides it from the choice; estimates already made with it stand.
 */

import type { CalculationMode } from '../types';

export type SpaceUse = CalculationMode | 'Advanced';

export const SPACE_USES: { key: SpaceUse; label: string }[] = [
  { key: 'FullRoom', label: 'Full room' },
  { key: 'CeilingOnly', label: 'Ceiling only' },
  { key: 'IndividualWalls', label: 'Individual walls' },
  { key: 'Advanced', label: 'Advanced' },
];

export type SpaceAccess = Record<string, Partial<Record<SpaceUse, boolean>>>;

const USE_KEYS = new Set<string>(SPACE_USES.map((u) => u.key));

/** The setting as stored (anything else, an array, a scalar: no switches). */
export function normalizeSpaceAccess(raw: unknown): SpaceAccess {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: SpaceAccess = {};
  for (const [id, flags] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^[a-z0-9-]{1,40}$/.test(id) || !flags || typeof flags !== 'object' || Array.isArray(flags)) continue;
    const row: Partial<Record<SpaceUse, boolean>> = {};
    for (const [k, v] of Object.entries(flags as Record<string, unknown>)) {
      if (USE_KEYS.has(k) && typeof v === 'boolean') row[k as SpaceUse] = v;
    }
    out[id] = row;
  }
  return out;
}

/** Is the space offered for this use? (Yes unless switched off.) */
export function spaceAllowed(access: SpaceAccess | undefined, id: string, use: SpaceUse): boolean {
  return access?.[id]?.[use] !== false;
}

/** The space types offered for a use -- and `keep` (the one already chosen) whatever its switch says. */
export function allowedSpaces<T extends { id: string }>(list: readonly T[], access: SpaceAccess | undefined, use: SpaceUse, keep?: string): T[] {
  return list.filter((t) => t.id === keep || spaceAllowed(access, t.id, use));
}
